'use client'

import Button from '@/components/ui/Button'
import { SELF_LEADERSHIP_QUESTIONS as QUESTIONS, SELF_LEADERSHIP_SCALE as SCALE } from '@/lib/assessment/selfLeadership'
import { cx } from '@/lib/utils'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import Image from 'next/image'
import { useRef, useState } from 'react'

const EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1]

const LAST_QUESTION_STEP = QUESTIONS.length
const STEP_EMAIL_GATE = LAST_QUESTION_STEP + 1
const STEP_SUCCESS = LAST_QUESTION_STEP + 2
const PROGRESS_DIVISOR = LAST_QUESTION_STEP + 1

const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 28 : -28, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir < 0 ? 28 : -28, opacity: 0 })
}

/** Intro hero + wizard share the same outer size */
const FRAME_OUTER = 'relative mx-auto mt-4 w-full min-w-0 max-w-6xl sm:mt-6'
const FRAME_MIN_H = 'min-h-[min(80vh,760px)]'
const FRAME_ROUNDED = 'rounded-[28px]'

/** Wizard card: extra horizontal inset so content never hugs / clips at the frame edge */
const WIZARD_CARD =
    'surface-strong relative flex min-h-0 flex-col overflow-x-clip overflow-y-hidden pt-8 pb-8 pl-9 pr-9 sm:pt-11 sm:pb-11 sm:pl-14 sm:pr-14 lg:px-16 lg:py-12'

export default function SelfLeadershipAssessmentSection() {
    const reduceMotion = useReducedMotion()
    const [step, setStep] = useState(0)
    const [direction, setDirection] = useState(0)
    const [answers, setAnswers] = useState<(number | null)[]>(() => Array(QUESTIONS.length).fill(null))
    const [email, setEmail] = useState('')
    const [consent, setConsent] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [flowStartedAt, setFlowStartedAt] = useState(() => Date.now())
    const honeypotRef = useRef<HTMLInputElement>(null)

    const questionIndex = step - 1
    const isIntro = step === 0
    const isQuestionStep = step >= 1 && step <= LAST_QUESTION_STEP
    const isEmailGate = step === STEP_EMAIL_GATE
    const isSuccess = step === STEP_SUCCESS

    const progress = isQuestionStep ? step / PROGRESS_DIVISOR : isEmailGate || isSuccess ? 1 : 0

    function go(next: number, dir: number) {
        setDirection(dir)
        setStep(next)
    }

    function setAnswer(index: number, value: number) {
        setAnswers((prev) => {
            const next = [...prev]
            next[index] = value
            return next
        })
    }

    function handleNext() {
        if (isIntro) {
            go(1, 1)
            return
        }
        if (!isQuestionStep) return
        if (answers[questionIndex] === null) return
        if (step >= 1 && step <= LAST_QUESTION_STEP - 1) {
            go(step + 1, 1)
        }
    }

    function handleBack() {
        if (step <= 0) return
        if (step === STEP_EMAIL_GATE) {
            go(LAST_QUESTION_STEP, -1)
            return
        }
        go(step - 1, -1)
    }

    function handleFinishQuestions() {
        if (answers.every((a): a is number => a !== null)) {
            setSubmitError(null)
            go(STEP_EMAIL_GATE, 1)
        }
    }

    async function handleSendResult(e: React.FormEvent) {
        e.preventDefault()
        setSubmitError(null)
        if (!email.trim()) {
            setSubmitError('Please enter your email address.')
            return
        }
        if (!consent) {
            setSubmitError('Please confirm that Nick Parry may reach out to you.')
            return
        }
        const resolved = answers.every((a): a is number => a !== null)
        if (!resolved) {
            setSubmitError('Something went wrong with your answers. Please start over.')
            return
        }

        setSubmitting(true)
        try {
            const res = await fetch('/api/assessment-result', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(),
                    answers,
                    consentContact: true as const,
                    website: honeypotRef.current?.value ?? '',
                    startedAt: flowStartedAt
                })
            })
            if (!res.ok) {
                setSubmitError('We could not send your result. Please try again in a moment.')
                return
            }
            go(STEP_SUCCESS, 1)
        } catch {
            setSubmitError('We could not send your result. Please try again in a moment.')
        } finally {
            setSubmitting(false)
        }
    }

    function restart() {
        setDirection(0)
        setStep(0)
        setAnswers(Array(QUESTIONS.length).fill(null))
        setEmail('')
        setConsent(false)
        setSubmitError(null)
        setSubmitting(false)
        setFlowStartedAt(Date.now())
        if (honeypotRef.current) honeypotRef.current.value = ''
    }

    const continueDisabled = isQuestionStep && answers[questionIndex] === null
    const isLastQuestion = step === LAST_QUESTION_STEP

    return (
        <section
            id='self-leadership-assessment'
            className='relative overflow-hidden bg-page text-text'
            aria-label='Self-leadership assessment'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute left-1/2 top-[10%] h-[520px] w-[880px] -translate-x-1/2 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_8%,transparent)] blur-[120px]' />
                <div className='absolute -right-40 bottom-0 h-[420px] w-[620px] rounded-full bg-[color-mix(in_oklab,var(--color-accent-2)_6%,transparent)] blur-[100px]' />
            </div>

            <div className='relative section-pad'>
                <div className='mx-auto min-w-0 max-w-7xl px-5 sm:px-8 lg:px-12'>
                    {!isIntro && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.25 }}
                            transition={{ duration: 0.65, ease: EASE }}
                            className='max-w-208'>
                            <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>SELF-CHECK</p>
                            <h2 className='mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl'>
                                Self-Leadership Assessment:
                                <span className='text-[color-mix(in_oklab,var(--color-accent)_85%,var(--color-text))]'>
                                    {' '}
                                    How well do you lead yourself?
                                </span>
                            </h2>
                            <p className='mt-4 text-pretty text-muted sm:text-lg'>
                                A short reflective walkthrough—rate each statement honestly. Your result is private in
                                the browser; nothing is stored or sent.
                            </p>
                        </motion.div>
                    )}

                    <AnimatePresence mode='wait'>
                        {isIntro ? (
                            <motion.div
                                key='intro-hero'
                                initial={{ opacity: 0, y: 22 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -16, transition: { duration: 0.35, ease: EASE } }}
                                transition={{ duration: 0.55, ease: EASE }}
                                className={FRAME_OUTER}>
                                <div
                                    role='region'
                                    aria-labelledby='assessment-hero-heading'
                                    className={cx(
                                        'relative overflow-hidden bg-dark text-on-dark shadow-[0_40px_100px_rgba(3,7,18,0.35)]',
                                        FRAME_MIN_H,
                                        FRAME_ROUNDED
                                    )}>
                                    <video
                                        className='absolute inset-0 h-full w-full object-cover'
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload='metadata'
                                        aria-hidden='true'>
                                        <source src='/test.mp4' type='video/mp4' />
                                    </video>
                                    <div
                                        className='absolute inset-0 bg-linear-to-b from-black/75 via-black/50 to-black/82'
                                        aria-hidden='true'
                                    />

                                    <div
                                        className={cx(
                                            'relative flex flex-col justify-center px-8 py-14 sm:px-14 sm:py-16 lg:px-16',
                                            FRAME_MIN_H
                                        )}>
                                        <motion.h2
                                            id='assessment-hero-heading'
                                            initial={{ opacity: 0, y: 14 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, ease: EASE }}
                                            className='max-w-208 text-balance text-3xl font-semibold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl'>
                                            How well do you{' '}
                                            <span className='bg-linear-to-r from-[#9ec0ff] to-[#8aefff] bg-clip-text text-transparent'>
                                                lead yourself
                                            </span>
                                            —right now?
                                        </motion.h2>

                                        <motion.p
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.55, ease: EASE, delay: 0.08 }}
                                            className='mt-6 max-w-184 text-pretty text-lg leading-relaxed text-muted-on-dark sm:text-xl'>
                                            A calm, private check-in—ten questions, about three minutes, and your result
                                            stays on this device.
                                        </motion.p>

                                        <motion.div
                                            initial={{ opacity: 0, y: 14 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.55, ease: EASE, delay: 0.16 }}
                                            className='mt-10'>
                                            <motion.div
                                                animate={reduceMotion ? undefined : { scale: [1, 1.06, 1] }}
                                                transition={
                                                    reduceMotion
                                                        ? undefined
                                                        : {
                                                              duration: 3.6,
                                                              repeat: Infinity,
                                                              ease: 'easeInOut'
                                                          }
                                                }
                                                className='inline-flex rounded-xl'>
                                                <Button
                                                    type='button'
                                                    variant='cta'
                                                    onClick={handleNext}
                                                    className='cursor-pointer px-8 py-3.5 text-base shadow-lg'>
                                                    Start the assessment
                                                </Button>
                                            </motion.div>
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key='wizard-shell'
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45, ease: EASE }}
                                className={FRAME_OUTER}>
                                <div className={cx(WIZARD_CARD, FRAME_MIN_H, FRAME_ROUNDED)}>
                                    <div
                                        aria-hidden='true'
                                        className='pointer-events-none absolute inset-y-0 left-0 z-0 w-1 bg-[linear-gradient(to_bottom,transparent,var(--color-accent),var(--color-accent-2),transparent)] sm:w-1.5'
                                    />

                                    {isQuestionStep && (
                                        <div className='relative z-[1] mb-6 min-w-0 shrink-0 sm:mb-8'>
                                            <div className='flex min-w-0 flex-wrap items-center justify-between gap-3'>
                                                <span className='text-[11px] font-semibold uppercase tracking-[0.22em] text-muted'>
                                                    Your progress
                                                </span>
                                                <span className='shrink-0 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] px-3.5 py-1 text-xs font-semibold tabular-nums tracking-tight text-[color-mix(in_oklab,var(--color-accent)_82%,var(--color-text))] ring-1 ring-[color-mix(in_oklab,var(--color-accent)_18%,transparent)]'>
                                                    {step} / {QUESTIONS.length}
                                                </span>
                                            </div>
                                            <div
                                                className='mt-3 h-2.5 overflow-hidden rounded-full bg-[color-mix(in_oklab,var(--color-accent)_8%,var(--color-soft-2))] shadow-[inset_0_1px_2px_rgba(3,7,18,0.06)] sm:h-3'
                                                role='progressbar'
                                                aria-valuenow={Math.round(progress * 100)}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                                aria-label='Assessment progress'>
                                                <motion.div
                                                    className='h-full rounded-full bg-[linear-gradient(90deg,var(--color-accent),var(--color-accent-2))] shadow-[0_0_12px_color-mix(in_oklab,var(--color-accent)_45%,transparent)]'
                                                    initial={false}
                                                    animate={{ width: `${progress * 100}%` }}
                                                    transition={{ type: 'spring', stiffness: 120, damping: 22 }}
                                                />
                                            </div>
                                            <p className='mt-2 text-right text-[11px] font-medium tabular-nums text-muted'>
                                                {Math.round(progress * 100)}% complete
                                            </p>
                                        </div>
                                    )}

                                    <div className='relative z-[1] min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain'>
                                        <AnimatePresence mode='wait' custom={direction}>
                                            {isQuestionStep && (
                                                <motion.div
                                                    key={`q-${questionIndex}`}
                                                    role='group'
                                                    aria-labelledby={`q-${questionIndex}-label`}
                                                    custom={direction}
                                                    variants={slideVariants}
                                                    initial='enter'
                                                    animate='center'
                                                    exit='exit'
                                                    transition={{ duration: 0.38, ease: EASE }}
                                                    className='mx-auto flex min-h-0 min-w-0 max-w-3xl flex-col justify-center gap-8 py-2 lg:min-h-[min(400px,52vh)] lg:gap-10 lg:py-6'>
                                                    <div className='rounded-2xl border border-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] bg-[color-mix(in_oklab,var(--color-soft)_98%,var(--color-accent)_4%)] p-6 shadow-[0_14px_40px_rgba(3,7,18,0.06)] sm:p-8'>
                                                        <p className='text-[11px] font-semibold uppercase tracking-[0.22em] text-muted'>
                                                            Reflection prompt
                                                        </p>
                                                        <p
                                                            id={`q-${questionIndex}-label`}
                                                            className='mt-4 text-pretty break-words text-xl font-semibold leading-snug tracking-tight text-text sm:text-2xl sm:leading-snug'>
                                                            <span className='mr-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[color-mix(in_oklab,var(--color-accent)_14%,transparent)] text-base font-bold tabular-nums text-[color-mix(in_oklab,var(--color-accent)_88%,var(--color-text))] ring-1 ring-[color-mix(in_oklab,var(--color-accent)_22%,transparent)] sm:mr-3 sm:h-9 sm:w-9 sm:text-lg'>
                                                                {step}
                                                            </span>
                                                            {QUESTIONS[questionIndex]}
                                                        </p>
                                                    </div>

                                                    <div className='grid gap-4'>
                                                        <div className='flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between'>
                                                            <p className='text-[11px] font-semibold uppercase tracking-[0.2em] text-muted'>
                                                                How much do you agree?
                                                            </p>
                                                            <p className='hidden text-xs text-muted sm:block'>
                                                                1 = strongly disagree · 5 = strongly agree
                                                            </p>
                                                        </div>
                                                        <div className='grid w-full min-w-0 grid-cols-2 gap-3 sm:grid-cols-5 sm:gap-3'>
                                                            {SCALE.map((s) => {
                                                                const selected = answers[questionIndex] === s.value
                                                                return (
                                                                    <motion.button
                                                                        key={s.value}
                                                                        type='button'
                                                                        whileTap={{ scale: 0.98 }}
                                                                        whileHover={selected ? { y: 0 } : { y: -2 }}
                                                                        onClick={() =>
                                                                            setAnswer(questionIndex, s.value)
                                                                        }
                                                                        aria-label={`${s.value}, ${s.label}`}
                                                                        aria-pressed={selected}
                                                                        className={cx(
                                                                            'relative w-full min-w-0 cursor-pointer rounded-2xl border px-3 py-4 text-center transition-all duration-200 sm:px-3 sm:py-5',
                                                                            selected
                                                                                ? 'z-[1] border-transparent bg-[linear-gradient(145deg,color-mix(in_oklab,var(--color-accent)_26%,var(--color-soft)),color-mix(in_oklab,var(--color-accent-2)_16%,var(--color-soft)))] shadow-[0_16px_36px_rgba(79,140,255,0.22)] ring-2 ring-[color-mix(in_oklab,var(--color-accent)_38%,transparent)] ring-offset-2 ring-offset-[color-mix(in_oklab,var(--color-soft)_96%,var(--color-accent)_4%)]'
                                                                                : 'border-[color-mix(in_oklab,var(--color-accent)_14%,transparent)] bg-[var(--color-soft)] shadow-sm hover:border-[color-mix(in_oklab,var(--color-accent)_32%,transparent)] hover:shadow-md'
                                                                        )}>
                                                                        {selected ? (
                                                                            <span
                                                                                className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--color-accent)_88%,#fff)] text-[10px] font-bold text-white shadow-sm'
                                                                                aria-hidden>
                                                                                ✓
                                                                            </span>
                                                                        ) : null}
                                                                        <span
                                                                            className={cx(
                                                                                'block text-2xl font-bold tabular-nums sm:text-[1.65rem]',
                                                                                selected
                                                                                    ? 'text-[color-mix(in_oklab,var(--color-accent)_55%,var(--color-text))]'
                                                                                    : 'text-text'
                                                                            )}>
                                                                            {s.value}
                                                                        </span>
                                                                        <span
                                                                            className={cx(
                                                                                'mt-2 block break-words text-[11px] font-medium leading-snug sm:text-xs',
                                                                                selected
                                                                                    ? 'text-[color-mix(in_oklab,var(--color-text)_88%,var(--color-accent))]'
                                                                                    : 'text-muted'
                                                                            )}>
                                                                            {s.short}
                                                                        </span>
                                                                    </motion.button>
                                                                )
                                                            })}
                                                        </div>
                                                        <div className='rounded-xl border border-[color-mix(in_oklab,var(--color-accent)_8%,transparent)] bg-[color-mix(in_oklab,var(--color-soft)_96%,var(--color-accent)_3%)] px-4 py-3 sm:px-5'>
                                                            <p className='text-xs leading-relaxed text-muted sm:text-sm'>
                                                                <span className='font-semibold text-text'>Tip:</span>{' '}
                                                                Pick what feels true for you <em>today</em>—not an ideal
                                                                version of yourself.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}

                                            {isEmailGate && (
                                                <motion.div
                                                    key='email-gate'
                                                    custom={direction}
                                                    variants={slideVariants}
                                                    initial='enter'
                                                    animate='center'
                                                    exit='exit'
                                                    transition={{ duration: 0.45, ease: EASE }}
                                                    className='grid min-h-0 min-w-0 grid-cols-1 gap-8 lg:min-h-[min(520px,58vh)] lg:grid-cols-[1fr_min(42%,380px)] lg:items-stretch lg:gap-10'>
                                                    <div className='flex min-w-0 flex-col justify-center gap-6 lg:py-2'>
                                                        <div>
                                                            <h3 className='text-balance text-2xl font-semibold tracking-tight sm:text-3xl'>
                                                                Curious about your result?
                                                            </h3>
                                                            <p className='mt-3 text-pretty text-muted sm:text-lg'>
                                                                Enter your email and I’ll send your personal snapshot.
                                                                I’ll reach out within 24 hours to see if you’d like to
                                                                go deeper.
                                                            </p>
                                                        </div>

                                                        <form
                                                            className='relative space-y-5'
                                                            onSubmit={handleSendResult}
                                                            noValidate>
                                                            <div>
                                                                <label
                                                                    htmlFor='assessment-email'
                                                                    className='text-[11px] font-semibold uppercase tracking-[0.2em] text-muted'>
                                                                    Email address
                                                                </label>
                                                                <input
                                                                    id='assessment-email'
                                                                    name='email'
                                                                    type='email'
                                                                    autoComplete='email'
                                                                    value={email}
                                                                    onChange={(ev) => setEmail(ev.target.value)}
                                                                    placeholder='you@company.com'
                                                                    className='mt-2 w-full cursor-text rounded-xl border border-transparent bg-[color-mix(in_oklab,var(--color-soft)_96%,var(--color-accent)_4%)] px-5 py-4 text-base text-text shadow-sm outline-none ring-1 ring-[color-mix(in_oklab,var(--color-accent)_10%,transparent)] transition-[box-shadow,ring] placeholder:text-muted focus:shadow-md focus:ring-2 focus:ring-[color-mix(in_oklab,var(--color-accent)_35%,transparent)]'
                                                                />
                                                            </div>

                                                            <label className='flex cursor-pointer gap-3 rounded-xl bg-[color-mix(in_oklab,var(--color-soft)_94%,var(--color-accent)_6%)] p-4 ring-1 ring-[color-mix(in_oklab,var(--color-accent)_10%,transparent)]'>
                                                                <input
                                                                    type='checkbox'
                                                                    checked={consent}
                                                                    onChange={(ev) => setConsent(ev.target.checked)}
                                                                    className='mt-1 size-4 shrink-0 cursor-pointer rounded border-[color-mix(in_oklab,var(--color-accent)_25%,transparent)] text-accent focus:ring-[color-mix(in_oklab,var(--color-accent)_35%,transparent)]'
                                                                />
                                                                <span className='text-sm leading-snug text-text'>
                                                                    I confirm that <strong>Nick Parry</strong> may
                                                                    contact me by email about my assessment result and
                                                                    related coaching information.
                                                                </span>
                                                            </label>

                                                            <input
                                                                ref={honeypotRef}
                                                                type='text'
                                                                name='website'
                                                                tabIndex={-1}
                                                                autoComplete='off'
                                                                aria-hidden='true'
                                                                className='absolute -left-[9999px] h-px w-px opacity-0'
                                                                defaultValue=''
                                                            />

                                                            {submitError ? (
                                                                <p
                                                                    className='text-sm font-medium text-red-700'
                                                                    role='alert'>
                                                                    {submitError}
                                                                </p>
                                                            ) : null}

                                                            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                                                                <button
                                                                    type='button'
                                                                    onClick={handleBack}
                                                                    className='cursor-pointer rounded-lg px-2 py-2 text-sm font-semibold text-muted underline-offset-4 hover:text-text hover:underline'>
                                                                    Back
                                                                </button>
                                                                <Button
                                                                    type='submit'
                                                                    variant='cta'
                                                                    disabled={submitting}
                                                                    className='w-full cursor-pointer px-8 py-3.5 text-base disabled:cursor-not-allowed sm:w-auto'>
                                                                    {submitting ? 'Sending…' : 'Receive test result'}
                                                                </Button>
                                                            </div>
                                                        </form>
                                                    </div>

                                                    <div className='relative -order-1 min-h-[240px] w-full overflow-hidden rounded-2xl shadow-[0_20px_50px_rgba(3,7,18,0.1)] ring-1 ring-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] sm:min-h-[280px] lg:order-none lg:-mr-9 lg:min-h-full lg:w-[calc(100%+2.25rem)] lg:rounded-none lg:rounded-r-[28px] lg:shadow-none lg:ring-0'>
                                                        <Image
                                                            src='/Nick-Parry.JPG'
                                                            alt='Nick Parry'
                                                            fill
                                                            sizes='(max-width: 1024px) 100vw, 380px'
                                                            className='object-cover object-[center_22%]'
                                                            priority
                                                        />
                                                    </div>
                                                </motion.div>
                                            )}

                                            {isSuccess && (
                                                <motion.div
                                                    key='success'
                                                    custom={direction}
                                                    variants={slideVariants}
                                                    initial='enter'
                                                    animate='center'
                                                    exit='exit'
                                                    transition={{ duration: 0.45, ease: EASE }}
                                                    className='grid min-h-0 min-w-0 grid-cols-1 gap-8 lg:min-h-[min(520px,58vh)] lg:grid-cols-[1fr_min(42%,380px)] lg:items-stretch lg:gap-10'>
                                                    <div className='flex min-w-0 flex-col justify-center gap-5 lg:gap-6 lg:py-2'>
                                                        <div className='inline-flex w-fit items-center gap-2 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_14%,transparent)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[color-mix(in_oklab,var(--color-accent)_85%,var(--color-text))]'>
                                                            <span
                                                                className='flex h-6 w-6 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--color-accent)_22%,var(--color-soft))] text-sm'
                                                                aria-hidden>
                                                                ✓
                                                            </span>
                                                            All done
                                                        </div>
                                                        <div className='rounded-2xl bg-[color-mix(in_oklab,var(--color-soft)_92%,var(--color-accent)_8%)] p-6 ring-1 ring-[color-mix(in_oklab,var(--color-accent)_10%,transparent)] sm:p-8'>
                                                            <h3 className='text-balance text-2xl font-semibold tracking-tight sm:text-3xl lg:text-[1.75rem]'>
                                                                You’re all set
                                                            </h3>
                                                            <p className='mt-4 text-pretty leading-relaxed text-muted sm:text-lg'>
                                                                Your snapshot is on its way—check your inbox (and spam,
                                                                just in case). I’ll reach out personally within the next{' '}
                                                                <strong className='text-text'>24 hours</strong>.
                                                            </p>
                                                            <p className='mt-4 text-sm leading-relaxed text-muted'>
                                                                If you don’t see an email within a few minutes, try
                                                                sending again from the assessment or write me directly
                                                                at{' '}
                                                                <a
                                                                    href='mailto:nickparry@ownleadership.de'
                                                                    className='cursor-pointer font-medium text-[color-mix(in_oklab,var(--color-accent)_78%,var(--color-text))] underline-offset-4 hover:underline'>
                                                                    nickparry@ownleadership.de
                                                                </a>
                                                                .
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <Button
                                                                type='button'
                                                                variant='cta'
                                                                onClick={restart}
                                                                className='w-full cursor-pointer px-8 py-3.5 text-base sm:w-auto'>
                                                                Take the assessment again
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div className='relative -order-1 min-h-[240px] w-full overflow-hidden rounded-2xl shadow-[0_20px_50px_rgba(3,7,18,0.1)] ring-1 ring-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] sm:min-h-[280px] lg:order-none lg:-mr-9 lg:min-h-full lg:w-[calc(100%+2.25rem)] lg:rounded-none lg:rounded-r-[28px] lg:shadow-none lg:ring-0'>
                                                        <Image
                                                            src='/Nick-Parry.JPG'
                                                            alt='Nick Parry'
                                                            fill
                                                            sizes='(max-width: 1024px) 100vw, 380px'
                                                            className='object-cover object-[center_22%]'
                                                        />
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {isQuestionStep && (
                                        <div className='relative z-[1] mt-auto flex min-w-0 shrink-0 flex-col-reverse gap-4 border-t border-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] bg-[linear-gradient(to_top,color-mix(in_oklab,var(--color-soft)_88%,transparent),transparent)] pt-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-3'>
                                            <button
                                                type='button'
                                                onClick={handleBack}
                                                className='inline-flex cursor-pointer items-center justify-center rounded-xl border border-[color-mix(in_oklab,var(--color-accent)_16%,transparent)] bg-[color-mix(in_oklab,var(--color-soft)_96%,var(--color-accent)_4%)] px-5 py-3 text-sm font-semibold text-text shadow-sm transition-[box-shadow,transform] duration-200 hover:border-[color-mix(in_oklab,var(--color-accent)_28%,transparent)] hover:shadow-md'>
                                                Back
                                            </button>
                                            <div className='flex min-w-0 shrink-0 flex-wrap justify-end gap-3 sm:justify-end'>
                                                {isLastQuestion ? (
                                                    <Button
                                                        type='button'
                                                        variant='cta'
                                                        onClick={handleFinishQuestions}
                                                        disabled={answers[questionIndex] === null}
                                                        className='cursor-pointer disabled:cursor-not-allowed'>
                                                        Get my result
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        type='button'
                                                        variant='cta'
                                                        onClick={handleNext}
                                                        disabled={continueDisabled}
                                                        className='cursor-pointer disabled:cursor-not-allowed'>
                                                        Continue
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    )
}
